import { useCallback, useMemo, useState } from 'react'
import { useUserSession } from 'features/session/context'

import {
  DashBoardItemData,
  DashBoardConditions,
  DashBoardConfigure
} from '../types'

import { profileToData, SaveToLocal } from '../profile'
import { DashBoard as DashBoardTable } from 'shared/indexedDb'

import { useSubSource } from 'features/dashBoard/hooks/subSource'

import { DashBoardProfileResult } from 'interface/dashBoardProfile'
import { useListMrpLog, useListMrpProject } from 'interface/carenet/mrp'
import { MrpLogResultList, MrpProjectResultList } from 'interface/carenet/mrp/types'
import { useListLvLog, useListLvProject } from 'interface/carenet/lv'
import { LvLogResultList, LvProjectResultList } from 'interface/carenet/lv/types'

type useDashBoardProps<T> = {
  profileKey: string
  configure: DashBoardConfigure<T>
  conditions: DashBoardConditions
  setConditions: (conditions: DashBoardConditions) => void
  profileId: number | null
  setProfileId: (profileId: number | null) => void
  searchId?: number,
  mrpProjectId?: number
  lvProjectCode?: string
}
export const useDashBoard = <T>({
  profileKey,
  configure,
  conditions,
  setConditions,
  profileId,
  setProfileId,
  searchId,
  mrpProjectId,
  lvProjectCode
}: useDashBoardProps<T>) => {
  const { userId } = useUserSession()

  const [dashBoardItems, setDashBoardItems] = useState<DashBoardItemData[]>([])
  const [globalAxisKey, setGlobalAxisKey] = useState<string | undefined>(undefined)
  const [selectedProfile, setSelectedProfile] = useState<DashBoardProfileResult | null>(null)

  const setCurrentProfile = useCallback(
    (profile: DashBoardProfileResult | null) => {
      if (profile) {
        setSelectedProfile({ ...profile, profileData: undefined })
        setProfileId(profile.profileId)
      } else {
        setSelectedProfile(null)
        setProfileId(null)
      }
    },
    [setProfileId, setSelectedProfile]
  )

  const onLoadProfile = useCallback(
    (profile: DashBoardProfileResult) => {
      const data = profileToData(profile.profileData)
      setDashBoardItems(data.items)
      setGlobalAxisKey(data.globalAxisKey)
      setCurrentProfile(profile)
    },
    [setDashBoardItems, setCurrentProfile]
  )

  const onLoadCookieData = useCallback(
    (cookieData: DashBoardTable) => {
      const data = profileToData(cookieData)
      setDashBoardItems(data.items)
      setGlobalAxisKey(data.globalAxisKey)
      setCurrentProfile(null)
    },
    [setDashBoardItems, setCurrentProfile]
  )

  const onDeleteProfileReply = useCallback(
    (profileId: number) => {
      if (selectedProfile?.profileId === profileId) {
        setCurrentProfile(null)
      }
    },
    [selectedProfile, setCurrentProfile]
  )

  const onChangeDashBoardItems = useCallback(
    (items: DashBoardItemData[]) => {
      setDashBoardItems(items)
      if (!selectedProfile || userId === selectedProfile.userId) {
        SaveToLocal(userId, profileKey, items, globalAxisKey, selectedProfile?.profileId ?? -1)
      }
    },
    [setDashBoardItems, userId, profileKey, selectedProfile, globalAxisKey]
  )
  const onChangeGlobalAxisKey = useCallback(
    (key: string | undefined) => {
      setGlobalAxisKey(key)
      if (!selectedProfile || userId === selectedProfile.userId) {
        SaveToLocal(userId, profileKey, dashBoardItems, key, selectedProfile?.profileId ?? -1)
      }
    },
    [setGlobalAxisKey, userId, profileKey, selectedProfile, dashBoardItems]
  )
  const hasGlobal = useMemo(
    () => dashBoardItems.some((item) => item.options?.some((line) => line.value === '@global') ?? false),
    [dashBoardItems]
  )

  const mrp = useSubSource<T, number, MrpProjectResultList, MrpLogResultList>({
    searchId, configure, source: 'mrp',
    dashBoardItems,
    findMaster: useListMrpProject,
    findData: useListMrpLog,
    defaultKeyValue: mrpProjectId ?? null
  })
  const lv = useSubSource<T, string, LvProjectResultList, LvLogResultList>({
    searchId, configure, source: 'lv',
    dashBoardItems,
    findMaster: useListLvProject,
    findData: useListLvLog,
    defaultKeyValue: lvProjectCode ?? null
  })

  const addDashBoardItem = useCallback(
    (itemData: DashBoardItemData) => onChangeDashBoardItems(dashBoardItems.concat([itemData])),
    [dashBoardItems, onChangeDashBoardItems]
  )

  return useMemo(() => ({
    profileKey, configure,
    dashBoardItems, onChangeDashBoardItems,
    hasGlobal, globalAxisKey, onChangeGlobalAxisKey,
    subSource: { mrp, lv },
    selectedProfile, profileId, setCurrentProfile, onLoadProfile, onLoadCookieData,
    conditions, setConditions,
    onDeleteProfileReply,
    addDashBoardItem
  }), [
    profileKey, configure,
    dashBoardItems, onChangeDashBoardItems,
    hasGlobal, globalAxisKey, onChangeGlobalAxisKey,
    mrp, lv,
    conditions, setConditions,
    selectedProfile, profileId, setCurrentProfile, onLoadProfile, onLoadCookieData,
    onDeleteProfileReply,
    addDashBoardItem
  ])
}
export type DashBoardValue<T> = ReturnType<typeof useDashBoard<T>>
